import { useEffect, useMemo, useState } from 'react';
import { apiPost } from '../api/apiClient';

// POST /api/trader-tags takes at most 200 addresses; stay well below it.
const BATCH_SIZE = 100;

/** The bulk endpoint's answer. `data_issues` is missing on an older indexer. */
interface BulkTagsResponse {
    results?: Record<string, string[]>;
    data_issues?: Record<string, string[]>;
}

export interface TraderTagData {
    /** address -> labels ("Whale", "Data Error", …) */
    tags: Map<string, string[]>;
    /**
     * address -> why the Data Error tag is set. Only traders with the tag have
     * an entry, and a missing entry (or an indexer without `data_issues`)
     * means no reasons are known.
     */
    dataIssues: Map<string, string[]>;
}

const EMPTY: TraderTagData = { tags: new Map(), dataIssues: new Map() };

/**
 * Labels, and the reasons behind Data Error, for a set of accounts, in as
 * many requests as it takes at most BATCH_SIZE addresses each. Keyed on the
 * sorted addresses, not on the collection: callers pass a fresh but equal Set
 * or array every render. `normalize` (a stable function) maps each address
 * before it is sent, e.g. to strip a prefix; results are keyed by what the
 * indexer answers.
 */
export function useTraderTagData(
    addresses: Iterable<string>,
    normalize?: (address: string) => string
): TraderTagData {
    const [data, setData] = useState<TraderTagData>(EMPTY);
    const key = useMemo(
        () =>
            [...addresses]
                .map(a => (normalize ? normalize(a) : a))
                .sort()
                .join(','),
        [addresses, normalize]
    );

    useEffect(() => {
        const list = key.split(',').filter(Boolean);
        if (list.length === 0) {
            setData(EMPTY);
            return;
        }

        const batches: string[][] = [];
        for (let i = 0; i < list.length; i += BATCH_SIZE) {
            batches.push(list.slice(i, i + BATCH_SIZE));
        }

        let cancelled = false;
        const controller = new AbortController();
        // allSettled: one failed batch must not blank the others' answers.
        Promise.allSettled(
            batches.map(batch =>
                apiPost<BulkTagsResponse>(
                    '/api/trader-tags',
                    { addresses: batch },
                    controller.signal
                )
            )
        )
            .then(outcomes => {
                if (cancelled) return;
                const tags = new Map<string, string[]>();
                const dataIssues = new Map<string, string[]>();
                let any = false;
                for (const outcome of outcomes) {
                    if (outcome.status !== 'fulfilled') continue;
                    const res = outcome.value;
                    if (res?.results) {
                        any = true;
                        for (const [k, v] of Object.entries(res.results)) {
                            tags.set(k, v);
                        }
                    }
                    for (const [k, v] of Object.entries(
                        res?.data_issues ?? {}
                    )) {
                        dataIssues.set(k, v ?? []);
                    }
                }
                if (any) setData({ tags, dataIssues });
            })
            .catch(err => {
                if (err?.name === 'AbortError') return;
            });

        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [key]);

    return data;
}
